import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  Form,
  Radio,
  Switch,
  Button,
  Space,
  Spin,
  message,
  Typography,
  Row,
  Col,
  Divider,
} from 'antd';
import { BellOutlined, MailOutlined, PhoneOutlined } from '@ant-design/icons';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';

const { Title, Text, Paragraph } = Typography;

interface Preferences {
  preferred_contact: 'email' | 'phone' | 'text';
  marketing_emails: boolean;
  marketing_sms: boolean;
  marketing_mail: boolean;
  appointment_reminders: boolean;
  birthday_cards: boolean;
  maintenance_reminders: boolean;
}

const DEFAULT_PREFERENCES: Preferences = {
  preferred_contact: 'email',
  marketing_emails: true,
  marketing_sms: false,
  marketing_mail: false,
  appointment_reminders: true,
  birthday_cards: true,
  maintenance_reminders: true,
};

const CustomerPreferences: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useCustomerAuth();
  const [form] = Form.useForm<Preferences>();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<Preferences | null>(null);

  const loadPreferences = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerPortalApi.get('/customer-portal/preferences');

      setPreferences(response.data);
      form.setFieldsValue(response.data);
    } catch (error) {
      console.error('Error loading preferences:', error);
      message.error('Failed to load preferences');
      // Seed full defaults so a subsequent Save submits a complete payload
      // (all 7 backend fields are required → a partial payload 400s).
      setPreferences(DEFAULT_PREFERENCES);
      form.setFieldsValue(DEFAULT_PREFERENCES);
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    loadPreferences();
  }, [isAuthenticated, navigate, loadPreferences]);

  const handleSave = async (values: Preferences) => {
    setSaving(true);
    try {
      await customerPortalApi.put('/customer-portal/preferences', values);

      setPreferences(values);
      message.success('Preferences saved successfully!');
    } catch (error) {
      console.error('Error saving preferences:', error);
      message.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <div
        style={{
          minHeight: '100vh',
          background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
          padding: '40px 20px',
        }}
      >
        <div style={{ maxWidth: 800, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0}>
              <Title level={3} style={{ margin: 0 }}>
                <BellOutlined /> Communication Preferences
              </Title>
              <Text type="secondary">
                Manage how we contact you and what updates you'd like to receive
              </Text>
            </Space>
          </Card>

          {/* Preferences Form */}
          <Card>
            <Form<Preferences>
              form={form}
              layout="vertical"
              onFinish={handleSave}
              initialValues={preferences || DEFAULT_PREFERENCES}
            >
              {/* Preferred Contact Method */}
              <Form.Item label={<Text strong>Preferred Contact Method</Text>}>
                <Form.Item name="preferred_contact" noStyle>
                  <Radio.Group>
                    <Space direction="vertical">
                      <Radio value="email">
                        <Space>
                          <MailOutlined />
                          <span>Email</span>
                        </Space>
                      </Radio>
                      <Radio value="phone">
                        <Space>
                          <PhoneOutlined />
                          <span>Phone</span>
                        </Space>
                      </Radio>
                      <Radio value="text">
                        <Space>
                          <PhoneOutlined />
                          <span>Text Message</span>
                        </Space>
                      </Radio>
                    </Space>
                  </Radio.Group>
                </Form.Item>
              </Form.Item>

              <Divider />

              {/* Marketing Communications */}
              <div style={{ marginBottom: 24 }}>
                <Title level={4}>Marketing & Promotions</Title>
                <Paragraph type="secondary">
                  Receive information about special offers, discounts, and new services.
                </Paragraph>

                <Form.Item label="Email Marketing" name="marketing_emails" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item label="Text Message Marketing" name="marketing_sms" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item label="Direct Mail" name="marketing_mail" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>

              <Divider />

              {/* Service Communications */}
              <div style={{ marginBottom: 24 }}>
                <Title level={4}>Service Communications</Title>
                <Paragraph type="secondary">
                  Important updates about your appointments and vehicle maintenance.
                </Paragraph>

                <Form.Item label="Appointment Reminders" name="appointment_reminders" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item label="Maintenance Reminders" name="maintenance_reminders" valuePropName="checked">
                  <Switch />
                </Form.Item>

                <Form.Item label="Birthday Cards & Special Offers" name="birthday_cards" valuePropName="checked">
                  <Switch />
                </Form.Item>
              </div>

              <Divider />

              {/* Save Button */}
              <Row gutter={16}>
                <Col>
                  <Button
                    type="primary"
                    size="large"
                    htmlType="submit"
                    loading={saving}
                  >
                    Save Preferences
                  </Button>
                </Col>
                <Col>
                  <Button
                    size="large"
                    onClick={() => {
                      form.resetFields();
                      loadPreferences();
                    }}
                  >
                    Reset
                  </Button>
                </Col>
              </Row>
            </Form>
          </Card>

          {/* Info Box */}
          <Card style={{ marginTop: 24, background: '#f0f2f5' }}>
            <Space direction="vertical" size="small">
              <Text strong>Privacy & Data</Text>
              <Text type="secondary" style={{ fontSize: 12 }}>
                We respect your privacy. Your communication preferences are stored securely
                and you can update them at any time. We'll never share your information with
                third parties without your consent.
              </Text>
            </Space>
          </Card>
        </div>
      </div>
    </PublicLayout>
  );
};

export default CustomerPreferences;
